import React, { useState } from 'react';

interface AppSettings {
  fontSize: 'small' | 'medium' | 'large';
  theme: 'dark' | 'light';
  defaultVersion: string;
  accessibilityMode?: boolean;
}

const SettingsPage: React.FC<{ 
  onBack?: () => void;
  currentSettings?: AppSettings;
  onSettingsChange?: (settings: AppSettings) => void;
}> = ({ onBack, currentSettings, onSettingsChange }) => {
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>(currentSettings?.fontSize || 'medium');
  const [theme, setTheme] = useState<'dark' | 'light'>(currentSettings?.theme || 'dark');
  const [defaultVersion, setDefaultVersion] = useState(currentSettings?.defaultVersion || 'KJV');
  const [accessibilityMode, setAccessibilityMode] = useState(currentSettings?.accessibilityMode || false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    const settings = { fontSize, theme, defaultVersion, accessibilityMode };
    localStorage.setItem('verlume_settings', JSON.stringify(settings));
    if (onSettingsChange) {
      onSettingsChange(settings);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{
      maxWidth: 600, margin: '0 auto', padding: '2rem 1.5rem',
      background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: 0,
      boxShadow: 'none', minHeight: '100vh', display: 'flex', flexDirection: 'column',
      transition: 'background 0.3s, color 0.3s'
    }}>
      <style>{`
        @media (max-width: 768px) {
          div[style*="maxWidth: 600"] {
            padding: 1.5rem 1rem !important;
            borderRadius: 0 !important;
          }
        }
        @media (max-width: 480px) {
          div[style*="maxWidth: 600"] {
            padding: 1rem 0.8rem !important;
            margin-top: 0 !important;
          }
          div[style*="maxWidth: 600"] h2 {
            font-size: 1.4rem !important;
          }
        }
      `}</style>
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', marginBottom: '1rem',
          display:'flex',alignItems:'center',gap:'0.3rem',fontSize:'1rem'
        }}
      >
        <span className="material-icons">arrow_back</span> Back
      </button>

      <h2 style={{
        color: 'var(--gold)', marginBottom: '1.5rem', fontWeight: 800,
        letterSpacing: '0.02em', fontFamily: 'Cinzel, serif'
      }}>
        Settings
      </h2>

      {/* Font Size */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{
          display: 'block', marginBottom: '0.8rem', fontWeight: 700,
          fontSize: '1.05rem', color: 'var(--gold)'
        }}>
          Font Size
        </label>
        <div style={{ display: 'flex', gap: '0.8rem' }}>
          {(['small', 'medium', 'large'] as const).map(size => (
            <button
              key={size}
              onClick={() => setFontSize(size)}
              style={{
                padding: '0.6rem 1.2rem', borderRadius: '6px',
                border: fontSize === size ? '2px solid var(--gold)' : '1px solid var(--bg-tertiary)',
                background: fontSize === size ? 'rgba(255,215,0,0.15)' : 'transparent',
                color: fontSize === size ? 'var(--gold)' : 'var(--text-secondary)',
                cursor: 'pointer', fontWeight: 600, minHeight: '44px',
                textTransform: 'capitalize'
              }}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Theme */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{
          display: 'block', marginBottom: '0.8rem', fontWeight: 700,
          fontSize: '1.05rem', color: 'var(--gold)'
        }}>
          Theme
        </label>
        <div style={{ display: 'flex', gap: '0.8rem' }}>
          {(['dark', 'light'] as const).map(th => (
            <button
              key={th}
              onClick={() => setTheme(th)}
              style={{
                padding: '0.6rem 1.2rem', borderRadius: '6px',
                border: theme === th ? '2px solid var(--gold)' : '1px solid var(--bg-tertiary)',
                background: theme === th ? 'rgba(255,215,0,0.15)' : 'transparent',
                color: theme === th ? 'var(--gold)' : 'var(--text-secondary)',
                cursor: 'pointer', fontWeight: 600, minHeight: '44px',
                textTransform: 'capitalize',
                display:'flex',alignItems:'center',gap:'0.4rem'
              }}
            >
              <span className="material-icons" style={{fontSize:'1.1rem'}}>{th === 'dark' ? 'dark_mode' : 'light_mode'}</span> {th}
            </button>
          ))}
        </div>
      </div>

      {/* Accessibility Mode */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{
          display: 'block', marginBottom: '0.8rem', fontWeight: 700,
          fontSize: '1.05rem', color: 'var(--gold)'
        }}>
          Accessibility Mode
        </label>
        <button
          onClick={() => setAccessibilityMode(!accessibilityMode)}
          style={{
            padding: '0.8rem 1.2rem', borderRadius: '6px',
            border: accessibilityMode ? '2px solid var(--gold)' : '1px solid var(--bg-tertiary)',
            background: accessibilityMode ? 'rgba(255,215,0,0.15)' : 'transparent',
            color: accessibilityMode ? 'var(--gold)' : 'var(--text-secondary)',
            cursor: 'pointer', fontWeight: 600, minHeight: '44px',
            display:'flex',alignItems:'center',gap:'0.6rem', fontSize: '1rem'
          }}
        >
          <span className="material-icons">{accessibilityMode ? 'check_circle' : 'radio_button_unchecked'}</span>
          {accessibilityMode ? 'Enabled' : 'Disabled'}
        </button>
        <p style={{
          marginTop: '0.6rem', fontSize: '0.9rem', color: 'var(--text-tertiary)',
          lineHeight: 1.5
        }}>
          High contrast colors, larger text, and enhanced screen reader support for better accessibility.
        </p>
      </div>

      {/* Default Version */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{
          display: 'block', marginBottom: '0.8rem', fontWeight: 700,
          fontSize: '1.05rem', color: 'var(--gold)'
        }}>
          Default Bible Version
        </label>
        <select
          value={defaultVersion}
          onChange={e => setDefaultVersion(e.target.value)}
          style={{
            width: '100%', background: 'var(--bg-secondary)', color: 'var(--gold)',
            border: '1.5px solid var(--gold)', borderRadius: '6px',
            padding: '0.8rem', fontSize: '1rem', fontWeight: 600,
            cursor: 'pointer', minHeight: '44px'
          }}
        >
          {['KJV', 'ASV', 'YLT', 'Darby', 'Webster', 'BSB', 'LEB', 'MKJV'].map(ver => (
            <option key={ver} value={ver}>{ver}</option>
          ))}
        </select>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        style={{
          width: '100%', background: 'var(--gold)', color: 'var(--bg-primary)',
          border: 'none', borderRadius: '6px', padding: '0.8rem',
          fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer',
          minHeight: '44px', marginTop: '1rem', transition: 'all 0.2s',
          display:'flex',alignItems:'center',justifyContent:'center',gap:'0.5rem'
        }}
      >
        <span className="material-icons">save</span> Save Settings
      </button>

      {saved && (
        <div style={{
          marginTop: '1rem', padding: '1rem', background: 'rgba(144, 238, 144, 0.2)',
          border: '1px solid #90ee90', borderRadius: '6px', color: '#90ee90',
          textAlign: 'center', fontWeight: 600, animation: 'fadeOut 2s forwards'
        }}>
          ✓ Settings saved successfully!
        </div>
      )}

      {/* Settings Info */}
      <div style={{
        marginTop: '2rem', padding: '1rem', background: 'rgba(255,215,0,0.08)',
        borderLeft: '3px solid var(--gold)', borderRadius: '4px', fontSize: '0.95rem',
        color: 'var(--text-secondary)', lineHeight: 1.6
      }}>
        <strong style={{ color: 'var(--gold)' }}><span className="material-icons" style={{fontSize:'1rem',display:'inline-block',verticalAlign:'middle',marginRight:'0.3rem'}}>info</span>Tips:</strong>
        <ul style={{ margin: '0.5rem 0 0 1.2rem', paddingLeft: 0 }}>
          <li>Your settings are saved locally on this device</li>
          <li>Highlights and notes are saved per Bible version</li>
          <li>Clear your browser cache to reset all data</li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsPage;
